import { ReactNode, useEffect, useState } from "react";
import { useSearchResultItems } from "@made-people/grease-search";
import {
  CentraSearchResultsProvider,
  CentraSearchResultsProviderProps,
} from "@made-people/grease-centra";
import { Product } from "@made-people/grease-product";

type QuickShopChildren = (
  products: Product[],
  baseProductId: string,
) => ReactNode;

export const QuickShopWithSiblings = ({
  initialPayload,
  children,
}: {
  initialPayload: CentraSearchResultsProviderProps["payload"];
  children: QuickShopChildren;
}) => {
  return (
    <CentraSearchResultsProvider
      payload={{
        ...initialPayload,
        limit: 1,
      }}
    >
      <InitialProductLoader initialPayload={initialPayload}>
        {children}
      </InitialProductLoader>
    </CentraSearchResultsProvider>
  );
};

const InitialProductLoader = ({
  initialPayload,
  children,
}: {
  initialPayload: CentraSearchResultsProviderProps["payload"];
  children: QuickShopChildren;
}) => {
  const [allProductIds, setAllProductIds] = useState<string[] | null>(null);
  const [baseProductId, setBaseProductId] = useState<string | null>(null);
  const items = useSearchResultItems();

  useEffect(() => {
    if (items && items.length > 0 && items[0].type === "product") {
      const product = items[0].product;
      const siblingProducts = product.siblingProducts || [];

      const siblingIds = siblingProducts
        .map((sibling) => sibling.productId)
        .filter((id) => id !== product.base.productId);

      const allIds = [product.base.productId, ...siblingIds];

      setBaseProductId(product.base.productId);
      setAllProductIds(allIds);
    }
  }, [items]);

  if (!allProductIds || !baseProductId) {
    return <QuickShopSkeleton />;
  }

  return (
    <CentraSearchResultsProvider
      payload={{
        ...initialPayload,
        products: allProductIds,
        limit: allProductIds.length,
      }}
    >
      <QuickShopProductsLoader baseProductId={baseProductId}>
        {children}
      </QuickShopProductsLoader>
    </CentraSearchResultsProvider>
  );
};

const QuickShopProductsLoader = ({
  baseProductId,
  children,
}: {
  baseProductId: string;
  children: QuickShopChildren;
}) => {
  const items = useSearchResultItems();

  if (!items || items.length === 0) {
    return <QuickShopSkeleton />;
  }

  const products = items
    .filter(
      (item): item is { type: "product"; product: Product } =>
        item.type === "product",
    )
    .map((item) => item.product);

  return <>{children(products, baseProductId)}</>;
};

const QuickShopSkeleton = () => (
  <div className="w-full max-w-screen-2xl mx-auto animate-pulse bg-white my-4 lg:my-12">
    <section className="grid grid-cols-12 gap-4 w-full p-10 lg:grid-cols-[1fr_2fr] h-[1002px] lg:h-[800px]"></section>
  </div>
);
